import React, { useState, useEffect, useCallback } from "react";
import i18n from "i18next";
import MerlinFeaturesPanel from "./MerlinFeaturesPanel";
import TranscriptModal from "./TranscriptModal";
import { VIDEO_AUDIO_FEATURES } from "./videoAudioFeatures";
import {
  startTranscribe,
  pollTranscribeJob,
  listTranscripts,
  matchForBox,
  updateTranscript,
} from "../../services/transcriptService";
import { getMediaDurationFromUrl, getUrlContentLength } from "../../utils/mediaDuration";
import {
  isTranscribeDurationTooLong,
  isTranscribeVideoTooLarge,
  transcribeDurationTooLongMessage,
  transcribeVideoTooLargeMessage,
} from "../../../common/upload_limits.es6";

// Video/audio Merlin drawer — mirrors the image Merlin drawer (RightIconStrip
// "Merlin" tab → this panel inside RightPanelShell). Clicking "Transcribe" opens
// a popup that does NOT auto-start: it shows an existing transcript if one
// exists, otherwise a Start button. Starting hands the job off to run in the
// background (job tray/widget); the result surfaces back here when it finishes.
// Detection is by query (never box state).
export default function VideoMerlinDrawer({ boxId, videoUrl, navId, trainingId, onOpenNarration }) {
  const [transcript, setTranscript] = useState(null); // matched idealStudioFiles row
  const [started, setStarted] = useState(false); // job handed off, awaiting result
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const refresh = useCallback(() => {
    if (!videoUrl) {
      setTranscript(null);
      return;
    }
    listTranscripts(trainingId)
      .then((rows) => setTranscript(matchForBox(rows, { state: { url: videoUrl } })))
      .catch(() => setTranscript(null));
  }, [videoUrl, trainingId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Start a transcription and hand it off to run in the background. Progress is
  // tracked by the job tray/widget — we don't block the popup; we just surface
  // the result here (refresh detection) once the worker finishes.
  const runTranscribe = async () => {
    if (!videoUrl) return;
    setError(null);

    // Hard guard (the backend mirrors this): block an over-limit video BEFORE
    // sending the job, so an oversized/too-long input can't break transcription.
    // Duration is read from the video's metadata (reliable); size via HEAD is
    // best-effort (may be CORS-blocked → null → left to the backend).
    const duration = await getMediaDurationFromUrl(videoUrl);
    if (isTranscribeDurationTooLong(duration)) {
      setError(transcribeDurationTooLongMessage());
      return;
    }
    const size = await getUrlContentLength(videoUrl);
    if (isTranscribeVideoTooLarge(size)) {
      setError(transcribeVideoTooLargeMessage());
      return;
    }

    setStarted(true);
    startTranscribe({ videoUrl, boxId, navId, trainingId, durationSeconds: duration ?? undefined })
      .then(({ jobId }) => pollTranscribeJob(jobId))
      .then((job) => {
        if (job.status === "failed") throw new Error(job.error || "transcription failed");
        setStarted(false);
        refresh();
      })
      .catch((e) => {
        setStarted(false);
        setError((e && e.message) || "transcription failed");
      });
  };

  // Narration needs a transcript (it voices the transcript text). Block it until
  // the video has been transcribed — the user transcribes first.
  const hasTranscript = !!(transcript && transcript.text);
  const features = VIDEO_AUDIO_FEATURES.map((f) =>
    f.id === "narration"
      ? {
          ...f,
          disabled: !hasTranscript,
          disabledHint: i18n.t("MerlinNarrationNeedsTranscript") || "Önce videoyu metne çevirin",
        }
      : f
  );

  // Transcribe → open the transcript popup (no auto-start). Narration → open the
  // normal AI voice sound modal (AddSoundModal "Anlatım Sesi" tab), pre-filled
  // with the transcript text. Only enabled once the video has been transcribed.
  const onSelectFeature = (id) => {
    if (id === "transcribe") {
      setError(null);
      setShowModal(true);
    } else if (id === "narration") {
      if (!hasTranscript) return;
      onOpenNarration?.(transcript ? transcript.text : "", navId);
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", minHeight: 0 }}>
      <MerlinFeaturesPanel features={features} onSelectFeature={onSelectFeature} />

      <TranscriptModal
        visible={showModal}
        transcript={transcript ? transcript.text : null}
        started={started}
        error={error}
        onStart={runTranscribe}
        onRetry={runTranscribe}
        onClose={() => setShowModal(false)}
        onSave={async (text) => {
          if (!transcript) return;
          await updateTranscript(transcript.id, text);
          setTranscript({ ...transcript, text });
        }}
        onOpenNarration={(text) => {
          // "Anlatım Sesi" straight from the transcript popup: close it and open
          // the AI voice sound modal seeded with whatever is in the textarea now
          // (unsaved corrections included).
          setShowModal(false);
          onOpenNarration?.(text || "", navId);
        }}
      />
    </div>
  );
}
